import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './AboutUs.css';
import { TopNav, SiteFooter } from './SiteChrome';
import { useLanguage } from './LanguageContext';
import { portfolioItems } from './portfolioData';

/* ── Portfolio Carousel ─────────────────────────────────── */
const PortfolioCarousel = ({ content }) => {
  const [current, setCurrent] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [animDir, setAnimDir] = useState('next'); // 'next' | 'prev'
  const intervalRef = useRef(null);
  const items = portfolioItems.filter(item => !item.isClientOnly);

  const goTo = (idx, dir = 'next') => {
    setAnimDir(dir);
    setCurrent((idx + items.length) % items.length);
  };

  // Auto advance
  useEffect(() => {
    if (isHovered) return;
    intervalRef.current = setInterval(() => {
      setAnimDir('next');
      setCurrent(prev => (prev + 1) % items.length);
    }, 3200);
    return () => clearInterval(intervalRef.current);
  }, [isHovered, items.length]);

  const item = items[current];
  const isOnline = item.status.toLowerCase() === 'online';

  return (
    <div
      className="about-carousel"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card */}
      <div className={`about-carousel-card anim-${animDir}`} key={current}>
        <div className="about-carousel-img-wrap">
          <img
            src={item.image}
            alt={item.name}
            className="about-carousel-img"
            loading="lazy"
          />
          <div className="about-carousel-img-overlay" aria-hidden="true" />
        </div>

        <div className="about-carousel-info">
          <div className="about-carousel-meta">
            {item.icon && (
              <img src={item.icon} alt="" className="about-carousel-favicon" />
            )}
            <span className="about-carousel-name">{item.name}</span>
            <span className={`about-carousel-status ${isOnline ? 'online' : 'offline'}`}>
              <span className="about-carousel-dot" />
              {isOnline
                ? (content.works?.statusOnline ?? 'Online')
                : (content.works?.statusOffline ?? 'Offline')}
            </span>
          </div>
          {item.tags && item.tags.length > 0 && (
            <div className="about-carousel-tags">
              {item.tags.map(t => <span key={t} className="about-carousel-tag">{t}</span>)}
            </div>
          )}
        </div>
      </div>

      {/* Dots */}
      <div className="about-carousel-dots" role="tablist" aria-label="Portfolio slides">
        {items.map((_, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === current}
            aria-label={`Slide ${i + 1}`}
            className={`about-carousel-dot-btn${i === current ? ' active' : ''}`}
            onClick={() => goTo(i, i > current ? 'next' : 'prev')}
          />
        ))}
      </div>

      {/* Arrows */}
      <button
        className="about-carousel-arrow left"
        aria-label="Previous"
        onClick={() => goTo(current - 1, 'prev')}
      >
        <span className="material-symbols-outlined">chevron_left</span>
      </button>
      <button
        className="about-carousel-arrow right"
        aria-label="Next"
        onClick={() => goTo(current + 1, 'next')}
      >
        <span className="material-symbols-outlined">chevron_right</span>
      </button>

      {/* CTA */}
      <Link to="/work" className="about-carousel-cta">
        <span>{content.nav?.works ?? 'Our Work'}</span>
        <span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
      </Link>
    </div>
  );
};

/* ── Page ───────────────────────────────────────────────── */
const AboutUs = () => {
  const { content } = useLanguage();

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('revealed'); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="about-page">
      <TopNav />

      <main className="about-main">


        <div className="content-container">
          {/* Hero / Filosofi */}
          <section className="hero-section">
            <div className="hero-grid">
              {/* Left: Text */}
              <div>
                <p className="section-label">{content.about.eyebrow}</p>

                {/* Title + inline star icon — wrapper handles side-by-side layout */}
                <div className="hero-title-wrap">
                  <h1
                    className="hero-title"
                    dangerouslySetInnerHTML={{ __html: content.about.title }}
                  />
                  <span className="hero-title-star" aria-hidden="true">
                    <span className="material-symbols-outlined">star</span>
                  </span>
                </div>

                <p className="hero-description">{content.about.description}</p>
              </div>

              {/* Right: Portfolio Carousel */}
              <div className="about-hero-visual">
                <PortfolioCarousel content={content} />
              </div>
            </div>
          </section>

          {/* Sejarah & Visi */}
          <section className="history-vision-section animate-fade-up delay-200">
            <div className="history-vision-grid">
              <div className="glass-panel reveal delay-100">
                <div className="panel-accent" aria-hidden="true" />
                <p className="panel-label">{content.about.historyLabel}</p>
                <h2 className="panel-title">{content.about.historyTitle}</h2>
                <p className="panel-text">{content.about.historyText}</p>
              </div>

              <div className="glass-panel reveal delay-200">
                <div className="panel-accent" aria-hidden="true" />
                <p className="panel-label">{content.about.visionLabel}</p>
                <h2 className="panel-title">{content.about.visionTitle}</h2>
                <p className="panel-text">{content.about.visionText}</p>
              </div>
            </div>
          </section>

          {/* Misi Grid */}
          <section className="mission-section animate-fade-up delay-300">
            <p className="section-label">{content.about.missionLabel}</p>
            <h2 className="hero-title">{content.about.missionTitle}</h2>

            <div className="missions-grid">
              {content.about.missions.map((mission, index) => {
                const icons = ['hub', 'trending_up', 'bolt', 'lightbulb'];
                return (
                  <div key={index} className={`mission-card reveal delay-${(index % 4 + 1) * 100}`}>
                    <div className="mission-icon">
                      <span className="material-symbols-outlined" aria-hidden="true">{icons[index]}</span>
                    </div>
                    <h3 className="mission-title">{mission.title}</h3>
                    <p className="mission-desc">{mission.text}</p>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default AboutUs;
